import React, { useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

function useViewTracker(contentId: string, active: boolean, enabled: boolean) {
  const stats = useRef({ ms: 0, pct: 0, sentValid: false, last: 0 });
  const send = () => {
    const s = stats.current;
    if (!enabled || s.ms < 500) return;
    api.post(`/contents/${contentId}/view`, { watched_ms: Math.round(s.ms), pct: Math.min(1, s.pct) }).catch(() => {});
  };
  useEffect(() => {
    if (!active) return;
    // Start ping: the server measures elapsed time from here to validate watch time.
    if (enabled) api.post(`/contents/${contentId}/view`, { watched_ms: 0, pct: 0 }).catch(() => {});
    return () => send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  const add = (deltaMs: number, pct: number) => {
    const s = stats.current;
    s.ms += deltaMs;
    s.pct = Math.max(s.pct, pct);
    if (!s.sentValid && (s.ms >= 3000 || s.pct >= 0.3)) {
      s.sentValid = true;
      send();
    }
  };
  return add;
}

function VideoMedia({ m, active, muted, onMute, track }: any) {
  const ref = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(false);
  const lastT = useRef(0);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (active) v.play().then(() => setPaused(false)).catch(() => setPaused(true));
    else v.pause();
  }, [active]);
  const onTime = () => {
    const v = ref.current!;
    const d = v.currentTime - lastT.current;
    lastT.current = v.currentTime;
    if (d > 0 && d < 1.5 && v.duration) track(d * 1000, v.currentTime / v.duration);
  };
  const toggle = () => {
    const v = ref.current!;
    if (v.paused) { v.play(); setPaused(false); } else { v.pause(); setPaused(true); }
  };
  return (
    <div className="absolute inset-0" onClick={toggle} data-testid="video-player">
      {m.thumb_url && <img src={mediaUrl(m.thumb_url)} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40" />}
      <video ref={ref} src={mediaUrl(m.url)} poster={mediaUrl(m.thumb_url)} muted={muted} loop playsInline preload="metadata"
        onTimeUpdate={onTime} onEnded={() => track(0, 1)} className="relative w-full h-full object-contain" />
      {paused && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Play size={64} className="text-white/80" fill="currentColor" /></div>}
      <button data-testid="mute-toggle" aria-label={muted ? "Attiva audio" : "Disattiva audio"} onClick={(e) => { e.stopPropagation(); onMute(); }}
        className="absolute top-20 md:top-6 right-4 w-10 h-10 bg-black/50 backdrop-blur border border-white/15 flex items-center justify-center hover:border-primary transition-colors">
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>
    </div>
  );
}

function ImagesMedia({ media, active, track }: any) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t0 = Date.now();
    let last = t0;
    const iv = setInterval(() => {
      const n = Date.now();
      track(n - last, Math.min(1, (n - t0) / 10000));
      last = n;
    }, 500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  return (
    <div className="absolute inset-0">
      <div className="flex h-full overflow-x-auto snap-x snap-mandatory no-scrollbar" onScroll={(e) => setIdx(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}>
        {media.map((m: any) => (
          <div key={m.id} className="relative w-full h-full shrink-0 snap-center overflow-hidden grain">
            <img src={mediaUrl(m.url)} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-40" />
            <img src={mediaUrl(m.url)} alt="Contenuto" loading="lazy" className="relative w-full h-full object-contain" />
          </div>
        ))}
      </div>
      {media.length > 1 && (
        <div className="absolute top-20 md:top-6 inset-x-0 flex justify-center gap-1" data-testid="gallery-dots">
          {media.map((m: any, i: number) => <span key={m.id} className={`h-1 w-6 ${i === idx ? "bg-primary" : "bg-white/30"}`} />)}
        </div>
      )}
    </div>
  );
}

export function MediaView({ content, active, muted, onMute, track = true }: any) {
  const add = useViewTracker(content.id, active, track && !content.is_mine);
  const media = content.media || [];
  if (media.some((m: any) => m.status !== "ready")) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/60" data-testid="media-processing">
        <Loader2 className="animate-spin" /> <span className="label">In elaborazione</span>
      </div>
    );
  }
  if (content.type === "video" && media[0]) return <VideoMedia m={media[0]} active={active} muted={muted} onMute={onMute} track={add} />;
  return <ImagesMedia media={media} active={active} track={add} />;
}
